/**
 * Web 项目与可选桌面壳之间的通信边界。
 *
 * 后台页面只通过这里访问桌面能力。浏览器模式下，Web 能力走 HTTP
 * 或浏览器 API；桌面模式下，才转发到 Tauri。禁止在页面组件里直接
 * import Tauri 包，后续桌面能力变更只需要维护这个文件。
 */

import { env } from '@/utils/env'
import { IpcCommand } from '@/types/ipc'

export type PlatformUnlisten = () => void

export interface AISidebarVisiblePayload {
  visible: boolean
  open?: boolean
}

export interface PlatformPosition {
  x: number
  y: number
}

export interface AISidebarGeometry {
  x: number
  y: number
  width: number
  height: number
  scaleFactor: number
}

type TauriWindow = Window & {
  __TAURI_INTERNALS__?: unknown
  __TAURI__?: {
    core?: {
      invoke?: <T>(command: string, args?: Record<string, unknown>) => Promise<T>
    }
  }
}

function getTauriWindow(): TauriWindow | null {
  return typeof window === 'undefined' ? null : (window as TauriWindow)
}

/** 判断当前页面是否运行在 Tauri；兼容远程 Web URL 下的全局 Tauri 注入。 */
export const isDesktopRuntime = (() => {
  const tauriWindow = getTauriWindow()
  return Boolean(tauriWindow?.__TAURI_INTERNALS__ || tauriWindow?.__TAURI__?.core?.invoke)
})()

function detectMacPlatform() {
  if (typeof navigator === 'undefined') return false
  const platform = navigator.platform || ''
  const userAgent = navigator.userAgent || ''
  return /Mac/i.test(platform) || (/Macintosh|Mac OS X/i.test(userAgent) && !/Windows/i.test(userAgent))
}

/** 当前桌面壳是否运行在 macOS；仅用于 macOS 专属客户端兜底交互。 */
export const isMacRuntime = isDesktopRuntime && detectMacPlatform()

function createRequestId(): string {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function firstText(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string') continue
    const text = value.trim()
    if (text) return text
  }
  return ''
}

function positiveNumber(...values: unknown[]): number {
  for (const value of values) {
    const number = Number(value || 0)
    if (Number.isFinite(number) && number > 0) return number
  }
  return 0
}

/** 将 Web 登录接口的 user/profile 响应转换为桌面端相同的用户信息结构。 */
function normalizeWebLoginResponse(data: Record<string, unknown>) {
  const rawUser = (data.user && typeof data.user === 'object' ? data.user : {}) as Record<string, unknown>
  const profile = (data.profile && typeof data.profile === 'object' ? data.profile : {}) as Record<string, unknown>
  const authority =
    (rawUser.authority && typeof rawUser.authority === 'object'
      ? rawUser.authority
      : {}) as Record<string, unknown>
  const authorityId = positiveNumber(rawUser.authorityId, rawUser.authority_id, authority.authorityId)

  return {
    ...data,
    user: {
      id: positiveNumber(rawUser.id, rawUser.ID),
      username: firstText(rawUser.username, rawUser.userName, rawUser.user_name),
      nickName: firstText(rawUser.nickName, rawUser.nick_name),
      phone: firstText(rawUser.phone),
      email: firstText(rawUser.email),
      avatar: firstText(rawUser.avatar, rawUser.headerImg, rawUser.header_img),
      companyId: positiveNumber(profile.companyId, rawUser.companyId, rawUser.company_id),
      departmentId: positiveNumber(profile.departmentId, rawUser.departmentId, rawUser.department_id),
      authorityId,
      roleId: positiveNumber(profile.companyRoleId, rawUser.roleId, authorityId),
      roleName: firstText(
        profile.companyRoleName,
        rawUser.roleName,
        authority.authorityName,
        authority.authority_name,
      ),
      dataScope: firstText(profile.dataScope, rawUser.dataScope) || 'self_only',
      companyName: firstText(profile.companyName, rawUser.companyName),
      companyLogo: firstText(profile.companyLogo, rawUser.companyLogo),
    },
  }
}

async function invokeTauri<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const tauriWindow = getTauriWindow()
  const globalInvoke = tauriWindow?.__TAURI__?.core?.invoke
  if (!tauriWindow?.__TAURI_INTERNALS__ && globalInvoke) {
    return globalInvoke<T>(command, args)
  }
  const { invoke } = await import('@tauri-apps/api/core')
  return invoke<T>(command, args)
}

/**
 * 通过 Tauri Channel 调用桌面流式命令。
 * Channel 的创建集中在平台层，业务模块不直接依赖 Tauri API。
 */
export async function invokeDesktopChannel<TMessage, TResult>(
  command: string,
  args: Record<string, unknown>,
  onMessage: (message: TMessage) => void,
): Promise<TResult> {
  if (!isDesktopRuntime) {
    throw new Error(`当前 Web 环境不支持桌面流式命令：${command}`)
  }

  const { Channel, invoke } = await import('@tauri-apps/api/core')
  const channel = new Channel<TMessage>(onMessage)
  return invoke<TResult>(command, { ...args, onEvent: channel })
}

async function invokeWeb<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const storageKey = (key: string) => `aihelper:web:${key}`

  switch (command) {
    case 'auth_get_token':
      return (localStorage.getItem('x-token') || '') as T
    case 'auth_sync_token':
      return undefined as T
    case 'auth_logout':
    case 'sidebar_close':
    case 'report_version':
      return undefined as T
    case 'endpoint_profile_info':
      return { present: false, valid: true } as T
    case 'api_base_url':
      return env.API_BASE_URL as T
    case 'cache_get': {
      const key = String(args?.key || '')
      return (localStorage.getItem(storageKey(key)) || null) as T
    }
    case 'cache_set': {
      const key = String(args?.key || '')
      localStorage.setItem(storageKey(key), String(args?.value || ''))
      return undefined as T
    }
    case 'cache_clear': {
      const prefix = String(args?.prefix || '')
      for (let i = localStorage.length - 1; i >= 0; i -= 1) {
        const key = localStorage.key(i)
        if (key?.startsWith(storageKey(prefix))) localStorage.removeItem(key)
      }
      return undefined as T
    }
    case 'device_get_id':
      return '' as T
    default:
      throw new Error(`当前 Web 项目不支持桌面命令：${command}`)
  }
}

/** 统一监听桌面事件；浏览器模式没有桌面事件时返回空清理函数。 */
export async function platformListen<T>(
  event: string,
  handler: (payload: T) => void,
): Promise<PlatformUnlisten> {
  if (!isDesktopRuntime) return () => {}

  const { listen } = await import('@tauri-apps/api/event')
  return listen<T>(event, (eventData) => handler(eventData.payload))
}

/** 统一发送桌面事件；浏览器模式不发送依赖 Tauri 的事件。 */
export async function platformEmit<T>(event: string, payload?: T): Promise<void> {
  if (!isDesktopRuntime) return

  const { emit } = await import('@tauri-apps/api/event')
  await emit(event, payload)
}

/** 向指定桌面窗口发送事件；浏览器模式下不执行跨窗口通信。 */
export async function platformEmitTo<T>(target: string, event: string, payload?: T): Promise<void> {
  if (!isDesktopRuntime) return

  const { emitTo } = await import('@tauri-apps/api/event')
  await emitTo(target, event, payload)
}

/** 获取当前桌面窗口的物理坐标；浏览器模式返回当前浏览器窗口坐标。 */
export async function getDesktopWindowPosition(): Promise<PlatformPosition> {
  if (!isDesktopRuntime) {
    return {
      x: typeof window === 'undefined' ? 0 : window.screenX,
      y: typeof window === 'undefined' ? 0 : window.screenY,
    }
  }

  const { getCurrentWindow } = await import('@tauri-apps/api/window')
  const position = await getCurrentWindow().outerPosition()
  return { x: position.x, y: position.y }
}

/** 获取当前桌面窗口的缩放比例；浏览器模式固定返回 1。 */
export async function getDesktopWindowScaleFactor(): Promise<number> {
  if (!isDesktopRuntime) return 1

  const { getCurrentWindow } = await import('@tauri-apps/api/window')
  return getCurrentWindow().scaleFactor()
}

/** 设置当前桌面窗口的逻辑尺寸；浏览器模式不调整浏览器窗口。 */
export async function setDesktopWindowSize(width: number, height: number): Promise<void> {
  if (!isDesktopRuntime) return

  const { getCurrentWindow, LogicalSize } = await import('@tauri-apps/api/window')
  await getCurrentWindow().setSize(new LogicalSize(width, height))
}

/** 设置当前桌面窗口的物理坐标；浏览器模式不调整浏览器窗口。 */
export async function setDesktopWindowPosition(x: number, y: number): Promise<void> {
  if (!isDesktopRuntime) return

  const { getCurrentWindow, PhysicalPosition } = await import('@tauri-apps/api/window')
  await getCurrentWindow().setPosition(new PhysicalPosition(x, y))
}

/** 开始拖动当前桌面窗口；该能力仅由桌面壳提供。 */
export async function startDesktopWindowDragging(): Promise<void> {
  if (!isDesktopRuntime) return

  const { getCurrentWindow } = await import('@tauri-apps/api/window')
  await getCurrentWindow().startDragging()
}

/** 监听当前桌面窗口的焦点变化；浏览器模式返回空清理函数。 */
export async function listenDesktopWindowFocus(
  handler: (focused: boolean) => void,
): Promise<PlatformUnlisten> {
  if (!isDesktopRuntime) return () => {}

  const { getCurrentWindow } = await import('@tauri-apps/api/window')
  return getCurrentWindow().onFocusChanged(({ payload }) => handler(payload))
}

/** 统一写入剪贴板，供 Web 页面和桌面 WebView 共用。 */
export async function writeClipboardText(text: string): Promise<void> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    throw new Error('当前环境不支持剪贴板写入')
  }
  await navigator.clipboard.writeText(text)
}

/** 将文本填入当前微信会话；该能力仅由桌面壳提供。 */
export async function sendTextToWechat(
  text: string,
  options: { confirmSend?: boolean } = {},
): Promise<void> {
  if (!isDesktopRuntime) {
    throw new Error('当前浏览器模式不支持向微信填入内容')
  }
  await platformInvoke('sidebar_send_to_wechat', {
    text,
    ...(options.confirmSend === undefined ? {} : { confirmSend: options.confirmSend }),
  })
}

/** 打开桌面端的 AI 监控侧边栏，浏览器模式不执行桌面命令。 */
export async function openAISidebar(): Promise<void> {
  if (!isDesktopRuntime) {
    throw new Error('当前浏览器模式不支持打开 AI 监控侧边栏')
  }

  await platformInvoke('sidebar_open')
}

/** 获取桌面端 AI 监控侧边栏状态，浏览器模式返回关闭状态。 */
export async function getAISidebarVisibleState(): Promise<AISidebarVisiblePayload> {
  if (!isDesktopRuntime) return { visible: false, open: false }

  return platformInvoke<AISidebarVisiblePayload>('sidebar_get_visible_state')
}

/**
 * 由 Tauri 重新读取微信窗口并同步侧边栏尺寸。
 * Web 层只消费返回结果，不维护另一套宽高计算逻辑。
 */
export async function syncAISidebarGeometry(): Promise<AISidebarGeometry | null> {
  if (!isDesktopRuntime) return null

  return platformInvoke<AISidebarGeometry>('sidebar_sync_geometry')
}

/** 显示并聚焦桌面端主窗口；可选携带主窗口需要打开的路由。 */
export async function showMainWindow(path?: string): Promise<void> {
  if (!isDesktopRuntime) return

  await platformInvoke('show_main_window', path ? { path } : undefined)
}

export async function openExternalUrl(url: string): Promise<void> {
  const value = url.trim()
  if (!value) return
  let target: string
  try {
    const parsed = new URL(value, window.location.href)
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
      throw new Error('unsupported_link')
    }
    target = parsed.href
  } catch {
    throw new Error('链接地址无效或不受支持')
  }

  if (isDesktopRuntime) {
    const { openUrl } = await import('@tauri-apps/plugin-opener')
    await openUrl(target)
    return
  }

  // noopener windows may return null even when opened; do not also navigate the current page.
  const link = document.createElement('a')
  link.href = target
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  try {
    link.click()
  } finally {
    link.remove()
  }
}

/** 按运行环境打开内部页面：桌面创建原生窗口，浏览器打开新标签页。 */
export async function openInternalPage(path: string, title?: string): Promise<void> {
  const target = path.trim()
  if (!target.startsWith('/') || target.startsWith('//')) {
    throw new Error('仅支持打开应用内部页面')
  }

  if (isDesktopRuntime) {
    await platformInvoke('open_app_window', {
      path: target,
      ...(title ? { title } : {}),
    })
    return
  }

  const url = `${window.location.origin}${window.location.pathname}#${target}`
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  try {
    link.click()
  } finally {
    link.remove()
  }
}

/** 调起当前 Tauri WebView 的系统打印窗口；浏览器模式由调用方使用 window.print 兜底。 */
export async function printCurrentDesktopWebview(): Promise<void> {
  if (!isDesktopRuntime) {
    throw new Error('当前 Web 环境不支持桌面打印')
  }
  await platformInvoke(IpcCommand.PrintCurrentWebview)
}

/** 持久化悬浮球的桌面坐标；浏览器模式不保存桌面位置。 */
export async function saveFloatingBallPosition(position: PlatformPosition): Promise<void> {
  if (!isDesktopRuntime) return

  await platformInvoke('floating_ball_set_position', { x: position.x, y: position.y })
}

/** 请求系统通知权限，Web 使用浏览器通知权限，桌面使用 Tauri 通知插件。 */
export async function requestSystemNotificationPermission(): Promise<boolean> {
  if (isDesktopRuntime) {
    const { isPermissionGranted, requestPermission } =
      await import('@tauri-apps/plugin-notification')
    if (await isPermissionGranted()) return true
    return (await requestPermission()) === 'granted'
  }

  if (typeof Notification === 'undefined') return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  return (await Notification.requestPermission()) === 'granted'
}

/** 发送系统通知，浏览器模式使用 Notification，桌面模式调用统一 IPC。 */
export async function sendSystemNotification(options: {
  title: string
  body?: string | null
}): Promise<void> {
  if (isDesktopRuntime) {
    await platformInvoke('system_notification_send', {
      title: options.title,
      body: options.body ?? null,
    })
    return
  }

  if (typeof Notification === 'undefined') return
  if (Notification.permission !== 'granted') return
  new Notification(options.title, { body: options.body || undefined })
}

async function requestJson<T>(path: string, body: unknown): Promise<T> {
  const token = localStorage.getItem('x-token') || ''
  const response = await fetch(`${env.API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Request-ID': createRequestId(),
      ...(token ? { 'x-token': token } : {}),
    },
    body: JSON.stringify(body),
  })
  const json = (await response.json()) as { code: number; data: T; msg: string }
  if (!response.ok || json.code !== 0) throw new Error(json.msg || `请求失败：${response.status}`)
  return json.data
}

/** 统一执行 IPC 或 Web 降级命令。 */
export async function platformInvoke<T>(
  command: string,
  args?: Record<string, unknown>,
): Promise<T> {
  if (isDesktopRuntime) return invokeTauri<T>(command, args)

  if (command === 'auth_login') {
    const data = await requestJson<Record<string, unknown>>('/base/login', {
      ...args,
      loginSource: 'client',
    })
    return normalizeWebLoginResponse(data) as T
  }
  if (command === 'auth_handle_token_expired' || command === 'auth_refresh_token') {
    try {
      const data = await requestJson<{ token?: string }>('/base/refreshToken', {})
      return { success: true, token: data.token } as T
    } catch {
      return { success: false, token: null } as T
    }
  }
  return invokeWeb<T>(command, args)
}

/** 获取当前运行环境的版本号，用于桌面壳和 Web 页面的统一展示。 */
export async function getRuntimeAppVersion(): Promise<string> {
  if (!isDesktopRuntime) return __APP_VERSION__
  const { getVersion } = await import('@tauri-apps/api/app')
  return getVersion()
}
